import config from 'constants/config'
import { isEnabledForFeature } from './featureFlagUtils'
import { reportError } from '../services/errorReportingService'
import { serialiseOccupancy } from './searchUtils'
import { countOccupants, initialRooms } from './occupancyUtils'

import type { Filters, GetLocationFlexRecommendations, PolygonMode } from 'api/recommendations'
import { DistanceUnit, kmToMiles } from 'components/OfferList/OfferListTiles/Support/searchDistanceLabel'
import uuidV4 from './uuidV4'
import { getPackageUniqueKey } from './offerUtils'
import { AbandonedCartViewModel, PersonalisedACState } from 'selectors/recommendations'
import React from 'react'

export function geoCriteriaMetForOfferBasedLere(countryCode: string) {
  return !isEnabledForFeature(config.LERE_OFFER_SEEDED_EXCLUDE_COUNTRY_CODES, `${countryCode}`)
}

export function geoCriteriaMetForCalendarRecommendations(offer: App.Offer | undefined) {
  try {
    const countryCode = offer?.property?.geoData?.countryCode
    return !isEnabledForFeature(config.LERE_CALENDAR_EXCLUDE_COUNTRY_CODES, `${countryCode}`)
  } catch (error) {
    reportError(error, { feature: 'lere-recommendations' })
    return false
  }
}

async function waitFor(ms = 500) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export async function waitOnCondition(shouldWait: () => boolean, intervalMS = 250, attemptsLeft = 2): Promise<number> {
  if (attemptsLeft === 0) {
    return attemptsLeft
  }
  if (shouldWait()) {
    await waitFor(intervalMS)
    return waitOnCondition(shouldWait, intervalMS, attemptsLeft - 1)
  }
  return attemptsLeft
}

export function lerePreCacheKey(offerId: string, region: string, filters: Pick<Filters, 'duration' | 'rooms'>) {
  const keys = [`offerId=${offerId}`, `region=${region}`]
  if (Number.isFinite(filters.duration)) {
    keys.push(`duration=${filters.duration}`)
  }
  if (filters.rooms) {
    keys.push(`rooms=${serialiseOccupancy(filters.rooms).join(';')}`)
  } else {
    keys.push(`rooms=${serialiseOccupancy([countOccupants(initialRooms)]).join(';')}`)
  }
  return keys.join(';')
}

interface LocationSeededCacheKeyParams {
  destinationId: string;
  polygonMode: PolygonMode;
  nearDestinations: boolean;
  checkIn?: string;
  checkOut?: string;
  duration?: number;
}

export function lereLocationSeededCacheKey({
  destinationId,
  polygonMode,
  nearDestinations,
  checkIn,
  checkOut,
  duration,
}: LocationSeededCacheKeyParams) {
  const keyParts = [
    `destinationId=${destinationId}`,
    `polygonMode=${polygonMode}`,
    `nearDestinations=${nearDestinations}`,
  ]
  if (checkIn && checkOut) {
    keyParts.push(`checkIn=${checkIn}`)
    keyParts.push(`checkOut=${checkOut}`)
  } else if (checkIn && duration !== undefined && duration > 0) {
    keyParts.push(`checkIn=${checkIn}`)
    keyParts.push(`duration=${duration}`)
  }
  return keyParts.join(';')
}

export function lereLocationFlexCacheKey({
  regionCode,
  destinationId,
  checkInDate,
  duration,
  numberOfAdults,
  childrenAges,
  flexibleDaysOnCheckIn,
}: GetLocationFlexRecommendations) {
  return [
    `regionCode=${regionCode}`,
    `destinationId=${destinationId}`,
    `checkInDate=${checkInDate}`,
    `duration=${duration}`,
    `numberOfAdult=${numberOfAdults}`,
    `childrenAges=${childrenAges}`,
    `flexibleDaysOnCheckIn=${flexibleDaysOnCheckIn}`,
  ].join(';')
}

export const lereLocationSeededStickyFooterParameters = {
  nonLppOfferPage: {
    polygonMode: 'polygon_restricted',
    nearDestinations: true,
  },
  searchResultsPage: {
    polygonMode: 'polygon_restricted',
    nearDestinations: true,
  },
} as const

// #region Destination landing page utils

export const lereDlpParameters = {
  mainList: {
    polygonMode: 'polygon_restricted',
    nearDestinations: true,
  },
  ymal: {
    polygonMode: 'polygon_excluded',
    nearDestinations: false,
  },
} as const

export function lereDlpMainListKey(destinationId: string) {
  return lereLocationSeededCacheKey({
    destinationId,
    polygonMode: lereDlpParameters.mainList.polygonMode,
    nearDestinations: lereDlpParameters.mainList.nearDestinations,
  })
}

export function lereDlpYmalListKey(destinationId: string) {
  return lereLocationSeededCacheKey({
    destinationId,
    polygonMode: lereDlpParameters.ymal.polygonMode,
    nearDestinations: lereDlpParameters.ymal.nearDestinations,
  })
}

// #endregion

let _lereSerendipityEnabled: boolean | undefined = undefined

// Percentage rollout just in case svc-search can't take so much new traffic.
export function isLereSerendipityEnabled() {
  try {
    if (_lereSerendipityEnabled !== undefined) {
      return _lereSerendipityEnabled
    }
    const isSSR = typeof IS_SSR !== 'undefined' && IS_SSR
    const threshold = Number(
      isSSR ?
        process.env.REACT_APP_LERE_SERENDIPITY_STRIP_ROLLOUT_PERCENTAGE :
        config.LERE_SERENDIPITY_STRIP_ROLLOUT_PERCENTAGE,
    )
    _lereSerendipityEnabled = Math.random() * 100 <= threshold && !!config.LERE_SERENDIPITY_TILES_VARIANT_EXPERIMENT_ID

    return _lereSerendipityEnabled
  } catch {
    return false
  }
}

export function getDistanceLabel(distanceKm: number, distanceFrom: string, distanceUnitOutput: DistanceUnit): string | null {
  if (distanceKm > 20) {
    return null
  }
  if (distanceUnitOutput === 'km') {
    if (distanceKm < 0.1) {
      const meters = (distanceKm * 1000).toFixed(1)
      return `${meters.replace(/\.0$/, '')}m from ${distanceFrom}`
    }

    return `${distanceKm.toFixed(1).replace(/\.0$/, '')}km from ${distanceFrom}`
  }
  if (distanceUnitOutput === 'mi') {
    const miles = kmToMiles(distanceKm)
    return `${miles.toFixed(1).replace(/\.0$/, '')} mi from ${distanceFrom}`
  }
  return null
}

export function recreateCartReduxSnapshot(abandonedCart: AbandonedCartViewModel): Partial<App.CartState> {
  const cartItem = abandonedCart
  // Copied from localStorage.getItem('cartV47') on prod
  const partialCart: any = {
    items: [{
      type: 'offer',
      id: uuidV4(),
      value: 0,
      price: abandonedCart.price,
      reservation: {
        taxesAndFees: undefined,
        durationSurchargeTotal: undefined,
        extraGuestSurcharge: abandonedCart.extraGuestSurcharge,
        propertyFees: abandonedCart.propertyFees,
      },
      transactionKey: abandonedCart.transactionKey,
      status: 'pending',
    }],
    experiences: {
      items: {},
      transfers: {},
      travellerForms: {},
      unavailableTickets: {},
      customerDetails: {},
      travellerDetails: [],
    },
    bedbank: {
      transactionKey: uuidV4(),
      rate: null,
      package: null,
      offer: null,
      sessionId: null,
      bedGroupId: null,
      bookingEmail: null,
      bookingPhonePrefix: null,
      bookingPhonePrefixCountryCode: null,
      bookingPhone: null,
      checkIn: null,
      checkOut: null,
      price: 0,
      rooms: [],
    },
    insurance: {
      transactionKey: uuidV4(),
      insuranceAccepted: false,
      insuranceTravellers: [],
      destinationCountries: [],
      numberOfSeniors: 0,
    } as any,
    flight: {
      transactionKey: uuidV4(),
      contactPhoneNumber: '',
      contactEmailAddress: '',
      basePriceSnapshot: 0,
      departingPriceSnapshot: 0,
      journey: {
        cost: 0,
        tax: 0,
        validatingCarrier: '',
        departing: {
          journeyKey: '',
          flights: [],
        },
        returning: {
          journeyKey: '',
          flights: [],
        },
        provider: '',
        isAllianceCarrier: false,
      },
      passengers: [
        {
          id: 'adult-0',
          type: 'adult',
          isPrimary: true,
          addedExtras: {
            baggage: {},
            carryOnBaggage: {},
          },
        },
        {
          id: 'adult-1',
          type: 'adult',
          isPrimary: false,
          addedExtras: {
            baggage: {},
            carryOnBaggage: {},
          },
        },
      ],
      creditDetails: null,
    } as any,
    accommodation: {
      package: {
        idSalesforceExternal: cartItem.packageId,
        uniqueKey: getPackageUniqueKey(cartItem.packageId, cartItem.duration, cartItem.roomRateId), // 'P:a0s2y000000cXoHAAUD:7R:9e7ca930-de60-11ed-b1fb-a929b9924a7a',
      } as any,
      offer: {
        id: cartItem.offer.id,
      } as any,
      originalPackageKey: getPackageUniqueKey(cartItem.packageId, cartItem.duration, cartItem.roomRateId),
      packageUpsellShown: false,
      reservationType: 'instant_booking',
      occupancies: cartItem.rooms,
      startDate: cartItem.checkIn,
      endDate: cartItem.checkOut,
      taxBreakdown: {},
      availableRooms: 0,
      flightsIncluded: false,
    },
    bookDatesOnly: false,
    isChangeDates: false,
    isChangePackage: false,
    currencyCode: abandonedCart.currency,
    giftCard: {
      transactionKey: uuidV4(),
      personalisation: {
        method: 'print',
        to: '',
        from: '',
        message: '',
        email_of_recipient: '',
        is_blank_message: false,
      },
      price: 0,
    },
    isGift: false,
    gift: null,
  }
  return partialCart
}

export function recreateCartV2ReduxSnapshot(abandonedCart: AbandonedCartViewModel, flightOriginCode: string): Partial<App.CheckoutCartState> {
  const cartRoom = abandonedCart.rooms[0]
  // Copied from localStorage.getItem('checkoutCartReduxState') on prod
  const partialCart: Partial<App.CheckoutCartState> = {
    cartId: abandonedCart.transactionKey,
    currencyCode: abandonedCart.currency,
    items: [
      {
        itemId: uuidV4(),
        offerId: abandonedCart.offer?.id,
        transactionKey: abandonedCart.transactionKey,
        itemType: abandonedCart.offer?.type,
        reservationType: 'instant_booking',
        packageId: abandonedCart.packageId,
        roomRateId: abandonedCart.roomRateId,
        duration: 7,
        flightBasePrice: 0,
        flightOriginCode,
        isBundled: false,
        occupancy: {
          roomId: '',
          adults: cartRoom?.adults || 0,
          children: cartRoom?.children || 0,
          infants: cartRoom?.infants || 0,
          childrenAge: cartRoom?.childrenAge || [],
        },
        checkIn: abandonedCart.checkIn,
        checkOut: abandonedCart.checkOut,
      } as any,
    ],
    specialRequests: {},
    insurance: null,
    transactionKey: abandonedCart.transactionKey,
    isGift: false,
    isDevToolsEnabled: false,
    roomsToBeConnected: [],
  }
  return partialCart
}

export function isTopPicksForMeEnabled() {
  return config.LERE_ENABLE_TOP_PICKS_FOR_ME
}

export type TPFMSkeletonTopSectionState = 'none' | 'cartSkeleton' | 'recentlyViewedSkeleton' | 'carts' | 'recentlyViewedOffers'

export function getTPFMSkeletonTopSectionState({
  topSectionStateRef,
  hasUserId,
  abandonedCartsWaitThresholdReached,
  skeletonWaitThresholdReached,
  personalisedAbandonedCarts,
  isRecentlyViewedLoading,
  recentlyViewedOffers,
}:{
  topSectionStateRef: React.MutableRefObject<TPFMSkeletonTopSectionState>,
  hasUserId: boolean,
  abandonedCartsWaitThresholdReached: boolean,
  skeletonWaitThresholdReached: boolean,
  personalisedAbandonedCarts: PersonalisedACState,
  isRecentlyViewedLoading: boolean,
  recentlyViewedOffers: App.OfferSummary[],
}): TPFMSkeletonTopSectionState {
  if (topSectionStateRef.current) {
    return topSectionStateRef.current
  }
  if (!hasUserId || skeletonWaitThresholdReached) {
    return 'none'
  }
  if ((personalisedAbandonedCarts.state === 'loading' || personalisedAbandonedCarts.fullState === 'loading') && !abandonedCartsWaitThresholdReached) {
    return 'cartSkeleton'
  }
  if ((personalisedAbandonedCarts.carts?.length ?? 0) > 0 && !abandonedCartsWaitThresholdReached) {
    topSectionStateRef.current = 'carts'
    return 'carts'
  }
  if (isRecentlyViewedLoading) {
    return 'recentlyViewedSkeleton'
  }
  if (!isRecentlyViewedLoading && recentlyViewedOffers.length) {
    topSectionStateRef.current = 'recentlyViewedOffers'
    return 'recentlyViewedOffers'
  }
  return 'none'
}
